import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// @mui
import { Card, Container, Box, CircularProgress, Typography } from '@mui/material';
// routes
import { PATH_MASTERDATA } from 'src/routes/paths';
// components
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import ProfileCover from 'src/sections/@dashboard/user/profile/ProfileCover';
import Profile from 'src/sections/@dashboard/user/profile/home/Profile';
import { useSnackbar } from 'src/components/snackbar';
// api
import userAPI from 'src/api/userAPI';

export default function UserDetailPage() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();
    const { id } = useParams();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // 🔹 Fetch user detail dari backend
    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoading(true);
                const response = await userAPI.getUserById(id);
                setUser(response.data?.data);
            } catch (error) {
                console.error(error);
                enqueueSnackbar('Failed to load user details', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchUser();
    }, [id, enqueueSnackbar]);

    if (loading) {
        return (
            <Container
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '70vh',
                    flexDirection: 'column',
                }}
            >
                <CircularProgress color="primary" />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading user detail...
                </Typography>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container>
                <Typography variant="h6" color="text.secondary">
                    User not found.
                </Typography>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>User Detail | Deposit Monitoring System</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'lg'}>
                <CustomBreadcrumbs
                    heading="User Detail"
                    links={[
                        { name: 'Master Data', href: PATH_MASTERDATA.root },
                        { name: 'Courier List', href: PATH_MASTERDATA.user },
                        { name: user.name },
                    ]}
                />

                {/* ✅ Cover Info */}
                <Card
                    sx={{
                        mb: 3,
                        height: 200,
                        position: 'relative',
                    }}
                >
                    <ProfileCover
                        name={user.name}
                        role={user.role?.name || '-'}
                        avatarUrl={user.avatar || '/assets/images/avatars/avatar_default.jpg'}
                        cover={user.site?.name || 'Unknown Site'}
                    />
                </Card>

                {/* ✅ Main Content */}
                <Box>
                    <Profile
                        info={{
                            id: user.id,
                            fullName: user.name,
                            nik: user.nik,
                            email: user.email,
                            phone: user.phone,
                            role: user.role?.name || '-',
                            site: user.site?.name || 'Unknown Site',
                            totalDeposit: user.total_deposit,
                            totalSalary: user.total_salary,
                            isFraud: user.is_fraud,
                            isActive: user.is_active,
                            address: user.address,
                            createdAt: user.joined_at,
                            updatedAt: user.updated_at,
                        }}
                    />
                </Box>
            </Container>
        </>
    );
}
